import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  UsersIcon,
  UserGroupIcon,
  FolderIcon,
  ServerIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import accountService from '../services/accountService';
import groupService from '../services/groupService';
import { getProjects } from '../services/projectService';
import searchService from '../services/searchService';
import { useAuth } from '../context/AuthContext';

const TELEGRAM_HISTORY_MIN_QUERY_LENGTH = 2;

const navigationItems = [
  { title: 'Dashboard', path: '/', icon: ServerIcon },
  { title: 'Accounts', path: '/accounts', icon: UsersIcon },
  { title: 'Groups', path: '/groups', icon: UserGroupIcon },
  { title: 'Projects', path: '/projects', icon: FolderIcon },
  { title: 'Processes', path: '/processes', icon: ClockIcon },
];

const searchModes = [
  { id: 'telegram-history', label: 'Telegram History' },
  { id: 'workspace', label: 'Workspace' },
];

function buildWorkspaceResults(query, accounts, groups, projects) {
  if (!query.trim()) {
    return [];
  }

  const searchTerm = query.toLowerCase();
  const results = [];

  accounts.forEach((account) => {
    if (
      account.name?.toLowerCase().includes(searchTerm) ||
      account.phonenumber?.toLowerCase().includes(searchTerm)
    ) {
      results.push({
        key: `account-${account.id}`,
        type: 'account',
        title: account.name,
        subtitle: account.phonenumber,
        path: `/accounts/${account.id}`,
        icon: UsersIcon,
      });
    }
  });

  groups.forEach((group) => {
    const username = group.chat_username || group.username;
    if (
      group.title?.toLowerCase().includes(searchTerm) ||
      username?.toLowerCase().includes(searchTerm)
    ) {
      results.push({
        key: `group-${group.chat_id}`,
        type: 'group',
        title: group.title,
        subtitle: `@${username || 'N/A'}${group.member_count ? ` • ${group.member_count} members` : ''}`,
        path: `/groups/${group.chat_id}`,
        icon: UserGroupIcon,
      });
    }
  });

  projects.forEach((project) => {
    if (
      project.name?.toLowerCase().includes(searchTerm) ||
      project.description?.toLowerCase().includes(searchTerm)
    ) {
      results.push({
        key: `project-${project.id}`,
        type: 'project',
        title: project.name,
        subtitle: project.description,
        path: `/projects/${project.id}`,
        icon: FolderIcon,
      });
    }
  });

  navigationItems.forEach((item) => {
    if (item.title.toLowerCase().includes(searchTerm)) {
      results.unshift({
        key: `navigation-${item.path}`,
        type: 'navigation',
        title: item.title,
        subtitle: 'Go to page',
        path: item.path,
        icon: item.icon,
      });
    }
  });

  return results.slice(0, 12);
}

function formatDate(timestamp) {
  if (!timestamp) {
    return '';
  }

  return new Date(timestamp * 1000).toLocaleString();
}

function buildTelegramSections(data) {
  if (!data) {
    return [];
  }

  const chats = Array.isArray(data.chats) ? data.chats : [];
  const messages = Array.isArray(data.messages) ? data.messages : [];
  const sections = [];

  if (messages.length > 0) {
    sections.push({
      key: 'telegram-messages',
      title: 'Messages',
      items: messages.map((message) => ({
        key: `telegram-message-${message.chatId}-${message.messageId}`,
        type: 'telegram_message',
        title: message.chatTitle,
        subtitle: [
          message.preview,
          message.accountNames?.length ? `Accounts: ${message.accountNames.join(', ')}` : null,
          message.externalUrl ? 'Opens in Telegram' : null,
          formatDate(message.date),
        ].filter(Boolean).join(' • '),
        path: message.tracked ? `/groups/${message.chatId}` : null,
        externalUrl: message.externalUrl || null,
        icon: ChatBubbleLeftRightIcon,
      })),
    });
  }

  if (chats.length > 0) {
    sections.push({
      key: 'telegram-chats',
      title: 'Chat name matches',
      items: chats.map((chat) => ({
        key: `telegram-chat-${chat.chatId}`,
        type: 'telegram_chat',
        title: chat.title,
        subtitle: [
          chat.username ? `@${chat.username}` : null,
          chat.accountNames?.length ? `Accounts: ${chat.accountNames.join(', ')}` : null,
          chat.source === 'tracked_subscription' ? 'Subscribed' : 'Public Telegram',
        ].filter(Boolean).join(' • '),
        path: chat.tracked ? `/groups/${chat.chatId}` : null,
        externalUrl: !chat.tracked ? chat.externalUrl : null,
        icon: UserGroupIcon,
      })),
    });
  }

  return sections;
}

function flattenSelectableItems(sections) {
  return sections.flatMap((section) =>
    section.items.filter((item) => item.path || item.externalUrl),
  );
}

const GlobalSearch = ({ isOpen, onClose, initialMode = 'telegram-history' }) => {
  const [query, setQuery] = useState('');
  const [activeMode, setActiveMode] = useState(initialMode);
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const { isAuthenticated } = useAuth();

  // Fetch all data
  const { data: accounts = [] } = useQuery(
    'accounts',
    accountService.getAccounts,
    { enabled: isAuthenticated }
  );
  const { data: groupsData } = useQuery('groups', () =>
    groupService.getGroups({}, { page: 1, pageSize: 100 }),
    { enabled: isAuthenticated }
  );
  const { data: projects = [] } = useQuery(
    'projects',
    getProjects,
    { enabled: isAuthenticated }
  );
  const {
    data: telegramHistoryData,
    isFetching: isTelegramHistoryLoading,
  } = useQuery(
    ['telegramHistorySearch', debouncedQuery],
    () => searchService.searchTelegramHistory(debouncedQuery, {
      chatLimit: 8,
      messageLimit: 20,
      historyChatScanLimit: 80,
      historyMessagesPerChat: 80,
    }),
    {
      enabled:
        isAuthenticated &&
        isOpen &&
        activeMode === 'telegram-history' &&
        debouncedQuery.trim().length >= TELEGRAM_HISTORY_MIN_QUERY_LENGTH,
      keepPreviousData: true,
      staleTime: 30000,
    }
  );

  const groups = groupsData?.groups || [];
  const workspaceResults = buildWorkspaceResults(query, accounts, groups, projects);
  const telegramSections = buildTelegramSections(telegramHistoryData);
  const workspaceSections = workspaceResults.length > 0
    ? [{ key: 'workspace-results', title: null, items: workspaceResults }]
    : [];
  const activeSections = activeMode === 'telegram-history' ? telegramSections : workspaceSections;
  const selectableItems = flattenSelectableItems(activeSections);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setActiveMode(initialMode);
    }

    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, initialMode]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [query]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query, activeMode]);

  const handleClose = () => {
    setQuery('');
    setDebouncedQuery('');
    setSelectedIndex(0);
    setActiveMode(initialMode);
    onClose();
  };

  const handleSelect = (result) => {
    if (result.externalUrl) {
      window.open(result.externalUrl, '_blank', 'noopener,noreferrer');
    } else if (result.path) {
      navigate(result.path);
    } else {
      return;
    }

    handleClose();
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => Math.min(prev + 1, Math.max(selectableItems.length - 1, 0)));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (selectableItems[selectedIndex]) {
            const selectedItem = selectableItems[selectedIndex];

            if (selectedItem.externalUrl) {
              window.open(selectedItem.externalUrl, '_blank', 'noopener,noreferrer');
            } else if (selectedItem.path) {
              navigate(selectedItem.path);
            } else {
              return;
            }

            setQuery('');
            setDebouncedQuery('');
            setSelectedIndex(0);
            setActiveMode(initialMode);
            onClose();
          }
          break;
        case 'Escape':
          setQuery('');
          setDebouncedQuery('');
          setSelectedIndex(0);
          setActiveMode(initialMode);
          onClose();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [initialMode, isOpen, navigate, onClose, selectableItems, selectedIndex]);

  if (!isAuthenticated) return null;
  if (!isOpen) return null;

  const getTypeColor = (type) => {
    const colors = {
      navigation: 'bg-blue-100 text-blue-700',
      account: 'bg-green-100 text-green-700',
      group: 'bg-purple-100 text-purple-700',
      project: 'bg-yellow-100 text-yellow-700',
      telegram_chat: 'bg-cyan-100 text-cyan-700',
      telegram_message: 'bg-orange-100 text-orange-700',
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  const showTelegramHint =
    activeMode === 'telegram-history' && query.trim().length < TELEGRAM_HISTORY_MIN_QUERY_LENGTH;
  const showEmptyState = query.trim() && activeSections.length === 0 && !isTelegramHistoryLoading;
  const telegramMeta = telegramHistoryData?.meta;
  const searchedAccountNames = telegramMeta?.searchedAccountNames || [];
  const historyScan = telegramMeta?.historyScan || {};
  const showTelegramCoverage =
    activeMode === 'telegram-history' &&
    debouncedQuery.trim().length >= TELEGRAM_HISTORY_MIN_QUERY_LENGTH &&
    telegramMeta;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-start justify-center pt-20">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl mx-4">
        {/* Search Input */}
        <div className="flex items-center border-b border-gray-200 p-4">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 mr-3" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={
              activeMode === 'telegram-history'
                ? 'Search Telegram chats and message history across all active accounts...'
                : 'Search accounts, groups, projects... (Ctrl+K)'
            }
            className="flex-1 outline-none text-gray-900 placeholder-gray-400"
          />
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 ml-3"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="border-b border-gray-100 px-4 py-3 flex items-center gap-2">
          {searchModes.map((mode) => (
            <button
              key={mode.id}
              onClick={() => setActiveMode(mode.id)}
              className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                activeMode === mode.id
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {mode.label}
            </button>
          ))}
        </div>

        {showTelegramCoverage && (
          <div className="border-b border-gray-100 px-4 py-2 bg-cyan-50 text-xs text-cyan-900 flex flex-wrap gap-x-4 gap-y-1">
            <span>Accounts: {searchedAccountNames.length || telegramMeta.accountsSearched || 0}</span>
            <span>
              Chats scanned: {historyScan.scannedChats || 0}
              {historyScan.totalCandidateChats ? `/${historyScan.totalCandidateChats}` : ''}
            </span>
            <span>Message matches: {telegramHistoryData?.messages?.length || 0}</span>
            <span>Chat name matches: {telegramHistoryData?.chats?.length || 0}</span>
          </div>
        )}

        {/* Results */}
        <div className="max-h-[28rem] overflow-y-auto">
          {showTelegramHint ? (
            <div className="p-8 text-center text-gray-500">
              <p>Type at least {TELEGRAM_HISTORY_MIN_QUERY_LENGTH} characters to search Telegram history.</p>
            </div>
          ) : isTelegramHistoryLoading ? (
            <div className="p-8 text-center text-gray-500">
              <p>Searching across active Telegram accounts…</p>
            </div>
          ) : showEmptyState ? (
            <div className="p-8 text-center text-gray-500">
              <p>
                {activeMode === 'telegram-history'
                  ? `No Telegram message or chat matches found for "${query}" across ${searchedAccountNames.length || telegramMeta?.accountsSearched || 0} active accounts.`
                  : `No results found for "${query}"`}
              </p>
            </div>
          ) : activeSections.length > 0 ? (
            <div className="py-2">
              {activeSections.map((section) => (
                <div key={section.key}>
                  {section.title && (
                    <div className="px-4 pt-3 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                      {section.title}
                    </div>
                  )}
                  <ul>
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      const selectableIndex = selectableItems.findIndex((candidate) => candidate.key === item.key);
                      const isSelected = selectableIndex === selectedIndex && selectableIndex !== -1;
                      const isClickable = Boolean(item.path || item.externalUrl);

                      return (
                        <li key={item.key}>
                          <button
                            onClick={() => isClickable && handleSelect(item)}
                            disabled={!isClickable}
                            className={`
                              w-full flex items-center px-4 py-3 transition-colors
                              ${isSelected ? 'bg-gray-100' : 'hover:bg-gray-50'}
                              ${!isClickable ? 'opacity-70 cursor-default' : ''}
                            `}
                          >
                            <div className="flex-shrink-0 mr-3">
                              <Icon className="h-5 w-5 text-gray-400" />
                            </div>
                            <div className="flex-1 text-left min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {item.title}
                              </p>
                              {item.subtitle && (
                                <p className="text-xs text-gray-500 truncate">
                                  {item.subtitle}
                                </p>
                              )}
                            </div>
                            <span className={`text-xs px-2 py-1 rounded ${getTypeColor(item.type)}`}>
                              {item.type}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
              {activeMode === 'telegram-history' && telegramHistoryData?.meta?.failedAccounts?.length > 0 && (
                <div className="px-4 py-3 text-xs text-amber-700 bg-amber-50 border-t border-amber-100">
                  Some accounts were skipped during search: {telegramHistoryData.meta.failedAccounts.map((item) => item.accountName).join(', ')}
                </div>
              )}
            </div>
          ) : (
            <div className="p-8 text-center text-gray-400">
              <MagnifyingGlassIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm">Start typing to search...</p>
              <p className="text-xs mt-2">
                {activeMode === 'telegram-history'
                  ? 'Find message text and chat names across active Telegram accounts.'
                  : 'Tip: Use arrow keys to navigate, Enter to select'}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-4 py-2 bg-gray-50 text-xs text-gray-500 flex items-center justify-between">
          <span>↑↓ Navigate • ↵ Select • ESC Close</span>
          <span className="text-gray-400">
            {activeMode === 'telegram-history' ? 'Ctrl+K opens Telegram History' : 'Ctrl+K to open'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default GlobalSearch;
